export type ThemeMode = 'light' | 'dark' | 'system';

export const ACCENT_COLORS = [
  'blue',
  'indigo',
  'violet',
  'purple',
  'fuchsia',
  'pink',
  'rose',
  'red',
  'orange',
  'amber',
  'yellow',
  'lime',
  'green',
  'emerald',
  'teal',
  'cyan',
  'sky',
  'slate',
  'gray',
  'zinc',
  'neutral',
  'stone',
  'brown',
  'copper',
  'custom',
] as const;

export type AccentColor = typeof ACCENT_COLORS[number];

export const ACCENT_COLOR_OPTIONS: { id: AccentColor; label: string; colorValue: string }[] = [
  { id: 'blue', label: 'Blue', colorValue: '#3b82f6' },
  { id: 'indigo', label: 'Indigo', colorValue: '#6366f1' },
  { id: 'violet', label: 'Violet', colorValue: '#8b5cf6' },
  { id: 'purple', label: 'Purple', colorValue: '#a855f7' },
  { id: 'fuchsia', label: 'Fuchsia', colorValue: '#d946ef' },
  { id: 'pink', label: 'Pink', colorValue: '#ec4899' },
  { id: 'rose', label: 'Rose', colorValue: '#f43f5e' },
  { id: 'red', label: 'Red', colorValue: '#ef4444' },
  { id: 'orange', label: 'Orange', colorValue: '#f97316' },
  { id: 'amber', label: 'Amber', colorValue: '#f59e0b' },
  { id: 'yellow', label: 'Yellow', colorValue: '#eab308' },
  { id: 'lime', label: 'Lime', colorValue: '#84cc16' },
  { id: 'green', label: 'Green', colorValue: '#22c55e' },
  { id: 'emerald', label: 'Emerald', colorValue: '#10b981' },
  { id: 'teal', label: 'Teal', colorValue: '#14b8a6' },
  { id: 'cyan', label: 'Cyan', colorValue: '#06b6d4' },
  { id: 'sky', label: 'Sky', colorValue: '#0ea5e9' },
  { id: 'slate', label: 'Slate', colorValue: '#64748b' },
  { id: 'gray', label: 'Gray', colorValue: '#6b7280' },
  { id: 'zinc', label: 'Zinc', colorValue: '#71717a' },
  { id: 'neutral', label: 'Neutral', colorValue: '#737373' },
  { id: 'stone', label: 'Stone', colorValue: '#78716c' },
  { id: 'brown', label: 'Brown', colorValue: '#a16207' },
  { id: 'copper', label: 'Copper', colorValue: '#c2410c' },
  { id: 'custom', label: 'Custom', colorValue: '#3b82f6' },
];

export const NEUTRAL_BASES = [
  'zinc',
  'slate',
  'gray',
  'neutral',
  'stone',
  'mauve',
  'olive',
] as const;

export type NeutralBase = typeof NEUTRAL_BASES[number];

export const NEUTRAL_BASE_OPTIONS: { id: NeutralBase; label: string; description: string; swatch: string }[] = [
  { id: 'zinc',    label: 'Zinc',    description: 'Cool neutral, no color cast',      swatch: '#71717a' },
  { id: 'slate',   label: 'Slate',   description: 'Cool blue-gray undertone',         swatch: '#64748b' },
  { id: 'gray',    label: 'Gray',    description: 'Balanced true gray',               swatch: '#6b7280' },
  { id: 'neutral', label: 'Neutral', description: 'Pure achromatic gray',             swatch: '#737373' },
  { id: 'stone',   label: 'Stone',   description: 'Warm brown-gray undertone',        swatch: '#78716c' },
  { id: 'mauve',   label: 'Mauve',   description: 'Subtle purple-gray undertone',     swatch: '#78718d' },
  { id: 'olive',   label: 'Olive',   description: 'Earthy green-gray undertone',      swatch: '#6b7264' },
];

export type DensityMode = 'compact' | 'cozy' | 'comfortable';

export type ContrastMode = 'standard' | 'high';

export interface BaseAppearancePreferences {
  themeMode: ThemeMode;
  accentColor: AccentColor;
  accentCustomHex?: string | null;
  neutralBase: NeutralBase;
  density: DensityMode;
  fontScale: number;
  contrastMode: ContrastMode;
  // Heading text color override (drives --brand-text-primary).
  // Kept as `fontCustomColor` for backwards-compat with v1 storage; semantically
  // this is "headings color" in the two-picker model. If only this is set and
  // fontBodyColor is null, body/tertiary/muted derive from this via CSS — same
  // behavior as v1.
  fontCustomColor?: string | null;
  // Body text color override (drives --brand-text-secondary; tertiary/muted
  // derive from it via CSS). Independent of fontCustomColor.
  fontBodyColor?: string | null;
  // Saved personal defaults. "Reset" returns here when set, else falls through
  // to the theme default. Heading + body are saved together as a pair.
  fontDefaultColor?: string | null;
  fontDefaultBodyColor?: string | null;
  // Per-picker force flags — bypass the WCAG contrast gate. Cleared on any
  // subsequent color change so each new color must be re-affirmed.
  fontCustomColorForce?: boolean;
  fontBodyColorForce?: boolean;
}

export type WallpaperId =
  | 'none'
  | 'night-mountain'
  | 'aurora'
  | 'abstract-waves'
  | 'desert-sunset'
  | 'cosmic'
  | 'forest'
  | 'ocean'
  | 'city-lights';

export type PresetTheme = 'classic' | 'modern' | 'high-contrast';

export interface EdgeMailAppearancePreferences extends BaseAppearancePreferences {
  wallpaper: WallpaperId;
  presetTheme: PresetTheme;
  wallpaperDim: number;
  wallpaperBlur: number;
  // EdgeMail-only text overrides. These drive the --em-text-* token layer
  // without changing the system-wide --brand-text-* appearance settings.
  mailTextPrimaryColor?: string | null;
  mailTextSecondaryColor?: string | null;
  mailTextDefaultPrimaryColor?: string | null;
  mailTextDefaultSecondaryColor?: string | null;
  mailTextPrimaryColorForce?: boolean;
  mailTextSecondaryColorForce?: boolean;
  mailSidebarTextColor?: string | null;
  mailSidebarTextDefaultColor?: string | null;
  mailSidebarTextColorForce?: boolean;
}
